import { useState } from "react"
import { Link, useHistory } from "react-router-dom"
import { useMutation } from "@apollo/client"
import { useTranslation } from "react-i18next"
import { Helmet } from "react-helmet"
import get from "lodash/get"
import isEmpty from "lodash/isEmpty"
import classNames from "classnames"
import { Form } from "react-bootstrap"
import { errorsVar, isLoggedInVar } from "cache"
import portal from "portalConfig"
import Hero from "components/shared/Hero"
import ErrorToast from "components/shared/ErrorToast"
import PhoneNumberInput from "components/shared/PhoneNumberInput"
import { AUTH_KEY, TEMP_SESSION_KEY } from "shared/constants"
import { useAuthRedirectEffect } from "components/Login/hooks"
import InfoCard from "components/shared/InfoCard"
import FullPageLoader from "components/shared/FullPageLoader"
import { Signup as SignUpUser } from "./operations"

import styles from "./Signup.module.css"

const Signup = () => {
  const { loading: isRedirecting } = useAuthRedirectEffect()
  const history = useHistory()
  const [t] = useTranslation()
  const { features } = portal.config

  const onCompleted = ({ signup }) => {
    const oaTag = get(signup, ["userSession", "oaTag"])
    const { errors } = signup

    if (!isEmpty(errors)) {
      if (errors.includes("email_address_redirect")) {
        history.push("/login")
      }
      errorsVar(errors)
    }

    if (oaTag) {
      localStorage.setItem(AUTH_KEY, oaTag)
      // Clear temp session when regular signup succeeds
      sessionStorage.removeItem(TEMP_SESSION_KEY)
      sessionStorage.removeItem("temp_session_email")
      errorsVar(null)
      isLoggedInVar(true)
    }
  }

  const [signupUser, { loading }] = useMutation(SignUpUser, {
    onCompleted
  })

  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [emailAddress, setEmailAddress] = useState("")
  const [password, setPassword] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [postalCode, setPostalCode] = useState("")
  const [validated, setValidated] = useState(false)

  const renderPhoneNumberError = (phone) => {
    if (!validated) {
      return null
    }
    if (isEmpty(phone) || phone.length < 4) {
      return "Please enter a valid phone number."
    }
    return null
  }

  const isValidPhoneNumber =
    validated && renderPhoneNumberError(phoneNumber) === null

  const handlePhoneNumberChange = (number) => {
    setPhoneNumber(number)
  }

  const handleSubmit = (e) => {
    const form = e.target

    e.preventDefault()
    if (form.checkValidity() === false) {
      e.stopPropagation()
    } else {
      signupUser({
        variables: {
          input: {
            firstName,
            lastName,
            emailAddress,
            password,
            phoneNumber,
            postalCode
          }
        }
      })
    }
    setValidated(true)
  }

  if (loading) return <FullPageLoader message="Creating your account..." />
  if (isRedirecting) return <FullPageLoader message="Redirecting..." />

  return (
    <>
      <Helmet title="Sign Up" />
      <Hero hideLogin>
        <Hero.Headline>
          Create an Account
          <br />
          to Continue
        </Hero.Headline>
      </Hero>

      <div className={classNames(styles.loginLine, "text-center")}>
        Are you a returning user?&nbsp;
        <Link to="/login" replace>
          Log In
        </Link>
      </div>

      <div className={classNames(styles.formWrapper, "container-sm")}>
        <div className="row justify-content-center">
          <div className="col-md-8 col-xl-6">
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="firstName" className="form-label fw-bold">
                  First name
                </label>
                <input
                  id="firstName"
                  className="form-control"
                  placeholder="Enter your first name"
                  value={firstName}
                  type="text"
                  required={features.userName}
                  onChange={(e) => {
                    setFirstName(e.target.value)
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  Please enter your first name.
                </Form.Control.Feedback>
              </div>
              <div className="mb-3">
                <label htmlFor="lastName" className="form-label fw-bold">
                  Last name
                </label>
                <input
                  id="lastName"
                  className="form-control"
                  placeholder="Enter your last name"
                  value={lastName}
                  type="text"
                  required={features.userName}
                  onChange={(e) => {
                    setLastName(e.target.value)
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  Please enter your last name.
                </Form.Control.Feedback>
              </div>
              <div className="mb-3">
                <label htmlFor="emailAddress" className="form-label fw-bold">
                  Email address
                </label>
                <input
                  id="emailAddress"
                  className="form-control"
                  placeholder="Enter your email address"
                  value={emailAddress}
                  type="email"
                  required
                  onChange={(e) => {
                    setEmailAddress(e.target.value)
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a valid email address.
                </Form.Control.Feedback>
              </div>
              <div className="mb-3">
                <label
                  htmlFor="phoneNumber"
                  className="form-label fw-bold d-flex align-items-center"
                >
                  Phone Number
                </label>
                <PhoneNumberInput
                  id="phoneNumber"
                  value={phoneNumber}
                  inputStyle={classNames(
                    {
                      [styles.validatedBorderStyle]: isValidPhoneNumber
                    },
                    styles.phoneNumberInputContainer
                  )}
                  buttonStyle={classNames(
                    {
                      [styles.validatedBorderStyle]: isValidPhoneNumber
                    },
                    styles.phoneNumberButtonStyle
                  )}
                  onChange={handlePhoneNumberChange}
                  displayError={!isEmpty(renderPhoneNumberError(phoneNumber))}
                  errorStyle={styles.phoneNumberErrorStyle}
                  errorButtonStyle={styles.phoneNumberErrorButtonStyle}
                />
                {renderPhoneNumberError(phoneNumber) && (
                  <div className="invalid-feedback d-block">
                    {renderPhoneNumberError(phoneNumber)}
                  </div>
                )}
              </div>
              {features.userPostalCode && (
                <div className="mb-3">
                  <label htmlFor="postalCode" className="form-label fw-bold">
                    Zip/Postal code
                  </label>
                  <input
                    id="postalCode"
                    className="form-control"
                    placeholder="Enter your Zip/Postal code"
                    value={postalCode}
                    type="text"
                    maxLength="10"
                    required={features.userPostalCode}
                    onChange={(e) => {
                      setPostalCode(e.target.value)
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please enter your Zip/Postal code.
                  </Form.Control.Feedback>
                </div>
              )}
              <div className="mb-3">
                <label htmlFor="password" className="form-label fw-bold">
                  Password
                </label>
                <input
                  id="password"
                  className="form-control"
                  placeholder="Enter your password"
                  type="password"
                  value={password}
                  required
                  onChange={(e) => {
                    setPassword(e.target.value)
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a valid password.
                </Form.Control.Feedback>
              </div>
              <div className="d-grid">
                <button
                  type="submit"
                  className={classNames(
                    styles.submitButton,
                    "btn btn-primary btn-lg mt-3 mb-4 fw-bold"
                  )}
                >
                  Create Account
                </button>
              </div>
            </Form>
            {features.userPhoneNumber &&
              isEmpty(t("updatingRecords.text")) &&
              !isEmpty(t("phoneNumber.text")) && (
                <InfoCard
                  header="More Information"
                  secondaryHeader={t("phoneNumber.header")}
                  text={t("phoneNumber.text")}
                />
              )}
            {!isEmpty(t("updatingRecords.text")) && (
              <InfoCard
                header="More Information"
                text={t("updatingRecords.text")}
              />
            )}
          </div>
        </div>
      </div>

      <ErrorToast />
    </>
  )
}

export default Signup
